import { useEffect, useRef, useState } from "react";
import { SortDirection, SortOrderBy } from "@/constants/enums";
import useShopFilterState from "@/viewModels/shop/features/useFilter/useShopFilterState";
import useSort from "@/viewModels/shop/features/useFilter/useSort";
import useRoastAttributes from "@/viewModels/shop/features/useFilter/useRoastAttributes";
import { shop_per_page } from "@/viewModels/shop/constants";
import type { ProductsQuery } from "@/viewModels/shop/features/useFilter/types";

type FilterViewModel = {
  filter: ProductsQuery;
  updateFilter: (updates: ProductsQuery) => void;
} & ReturnType<typeof useSort> &
  ReturnType<typeof useRoastAttributes>;

const useFilter = ({
  currentCategorySlug,
}: {
  currentCategorySlug: string | undefined;
}): FilterViewModel => {
  const { getFilterFromURL, saveFilterToURL } = useShopFilterState();

  const defaultFilter: ProductsQuery = {
    per_page: shop_per_page,
    attributes: undefined,
    orderby: SortOrderBy.DATE,
    order: SortDirection.DESC,
    category: currentCategorySlug,
  };

  // 初始化 filter：優先從 URL 恢復
  const initialFilter = getFilterFromURL()
    ? { ...defaultFilter, ...getFilterFromURL() } // 保留不在 URL 的 filter 參數
    : defaultFilter;

  const [filter, setFilter] = useState(initialFilter);

  const isMountedRef = useRef(false);

  // Filter 變更時同步到 URL
  const updateFilter = (updates: ProductsQuery) => {
    setFilter((prev) => ({ ...prev, ...updates }));
  };

  const { selectedSortOption, updateSelectedSortOption } = useSort({
    filter,
    updateFilter,
  });

  const { roastAttrTerms, selectedRoastTerm, isLoadingRoastAttr, updateSelectedRoastAttrTerm } =
    useRoastAttributes({
      filter,
      updateFilter,
    });

  const resetToDefaultFilter = (currentCategorySlug: string | undefined) => {
    setFilter({ ...initialFilter, category: currentCategorySlug });
  };

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    resetToDefaultFilter(currentCategorySlug);
  }, [isMountedRef, currentCategorySlug]);

  useEffect(() => {
    saveFilterToURL(filter);
  }, [filter]);

  return {
    filter,
    updateFilter,

    selectedSortOption,
    updateSelectedSortOption,

    roastAttrTerms,
    selectedRoastTerm,
    isLoadingRoastAttr,
    updateSelectedRoastAttrTerm,
  };
};

export default useFilter;
